import { Building2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { useDepartments } from "@/hooks/org/use-departments";

// ---------------------------------------------------------------------------
// DeptOrTextInput — dept picker that populates the text field, or free text
// ---------------------------------------------------------------------------

export interface DeptOrTextInputProps {
  label: string;
  textValue: string;
  deptId: string;
  onTextChange: (v: string) => void;
  onDeptChange: (deptId: string, deptCode: string) => void;
  placeholder?: string;
  inputId?: string;
}

export function DeptOrTextInput({
  label,
  textValue,
  deptId,
  onTextChange,
  onDeptChange,
  placeholder = "Ex: DMC",
  inputId,
}: DeptOrTextInputProps) {
  const { departments } = useDepartments();
  // Only show departments that have a code
  const depts = departments.filter((d) => d.code);

  function handleDeptSelect(id: string) {
    if (id === "__clear__") {
      onDeptChange("", "");
      return;
    }
    const dept = depts.find((d) => d.id === id);
    if (dept) onDeptChange(dept.id, dept.code ?? dept.name);
  }

  return (
    <div className="space-y-1">
      <Label htmlFor={inputId} className="text-xs">{label}</Label>
      <div className="flex gap-1.5">
        {/* Free-text input */}
        <Input
          id={inputId}
          className="h-8 text-xs flex-1"
          value={textValue}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={placeholder}
        />
        {/* Dept picker */}
        <Select value={deptId || "__none__"} onValueChange={handleDeptSelect}>
          <SelectTrigger className="h-8 w-10 shrink-0 px-0 flex items-center justify-center" title="Selecionar departamento">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          </SelectTrigger>
          <SelectContent align="end">
            {deptId && (
              <SelectItem value="__clear__">
                <span className="flex items-center gap-1.5 text-destructive">
                  <X className="h-3 w-3" /> Limpar departamento
                </span>
              </SelectItem>
            )}
            {depts.map((d) => (
              <SelectItem key={d.id} value={d.id} className="text-xs">
                <span className="font-mono font-semibold">{d.code}</span>
                <span className="ml-1.5 text-muted-foreground">{d.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {deptId && (
        <p className="text-[10px] text-muted-foreground leading-none">
          Ligado ao departamento
        </p>
      )}
    </div>
  );
}
